<template>
  <div>

    <!-- Page Header -->
    <div class="page-header">
      <div class="page-header-left">
        <button type="button" @click="$router.go(-1)" class="btn-back">
          <font-awesome-icon icon="arrow-left" />
        </button>
        <div>
          <h5 class="page-title">
            Payslip
            <span v-if="details?.period?.reference" class="ref-pill">{{ details.period.reference }}</span>
            <span class="status-chip" :class="statusClass(details?.period?.status)" v-if="details?.period?.status">
              {{ capitalize(details.period.status) }}
            </span>
          </h5>
          <p class="page-sub" v-if="details?.period?.payroll_month">{{ details.period.payroll_month }} &bull; {{ capitalize(details?.period?.period_frequency) }}</p>
        </div>
      </div>
      <div class="page-actions" v-if="details">
        <button class="btn-outline" @click="downloadPDF">
          <font-awesome-icon icon="file-pdf" class="icon-red" /> Download PDF
        </button>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="skeleton-wrap">
      <div class="sk sk-hdr"></div>
      <div class="sk-row">
        <div class="sk sk-half"></div>
        <div class="sk sk-half"></div>
      </div>
      <div class="sk sk-body"></div>
      <div class="sk sk-footer"></div>
    </div>

    <!-- Payslip Document -->
    <div v-else-if="details" ref="payslipRef" class="slip">

      <!-- ══ DOCUMENT HEADER ══ -->
      <div class="slip-head">
        <div class="slip-head-accent"></div>
        <div class="slip-head-body">
          <div class="slip-company">
            <img v-if="details?.period?.business?.logo" :src="host + details.period.business.logo" alt="Logo" class="slip-logo" />
            <div v-else class="slip-logo-placeholder">
              <font-awesome-icon icon="building" />
            </div>
            <div>
              <p class="slip-company-name">{{ details?.period?.business?.name }}</p>
              <p class="slip-company-meta">{{ details?.period?.business?.email }}</p>
              <p class="slip-company-meta">{{ details?.period?.business?.phone_number }}</p>
              <p class="slip-company-meta">{{ details?.period?.business?.address }}</p>
            </div>
          </div>
          <div class="slip-head-right">
            <div class="slip-title">PAYSLIP</div>
            <div class="slip-meta-block">
              <div class="slip-meta-row">
                <span class="slip-meta-key">Reference</span>
                <span class="slip-meta-val">{{ details?.period?.reference || '—' }}</span>
              </div>
              <div class="slip-meta-row">
                <span class="slip-meta-key">Pay Month</span>
                <span class="slip-meta-val">{{ details?.period?.payroll_month || '—' }}</span>
              </div>
              <div class="slip-meta-row">
                <span class="slip-meta-key">Pay Date</span>
                <span class="slip-meta-val">{{ formatDate(details?.period?.payment_date) }}</span>
              </div>
              <div class="slip-meta-row">
                <span class="slip-meta-key">Period</span>
                <span class="slip-meta-val">{{ formatDate(details?.period?.start_date) }} – {{ formatDate(details?.period?.end_date) }}</span>
              </div>
              <div class="slip-meta-row">
                <span class="slip-meta-key">Frequency</span>
                <span class="slip-meta-val">{{ capitalize(details?.period?.period_frequency) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══ EMPLOYEE + PAYMENT INFO STRIP ══ -->
      <div class="info-strip">
        <div class="info-col">
          <p class="info-heading">Employee Details</p>
          <div class="info-row"><span class="info-key">Full Name</span><span class="info-val fw">{{ details?.employee?.first_name }} {{ details?.employee?.last_name }}</span></div>
          <div class="info-row"><span class="info-key">Employee ID</span><span class="info-val">{{ details?.employee?.employee_no || '—' }}</span></div>
          <div class="info-row" v-if="details?.period?.country === 'GB'"><span class="info-key">NI Number</span><span class="info-val">{{ details?.employee?.nin || '—' }}</span></div>
          <div class="info-row"><span class="info-key">Tax Code / ID</span><span class="info-val">{{ details?.employee?.tax_code || '—' }}</span></div>
        </div>
        <div class="info-divider"></div>
        <div class="info-col">
          <p class="info-heading">Payment Details</p>
          <div class="info-row"><span class="info-key">Pay Run</span><span class="info-val fw">{{ details?.period?.name || '—' }}</span></div>
          <div class="info-row"><span class="info-key">Pay Method</span><span class="info-val">Bank Transfer</span></div>
          <div class="info-row"><span class="info-key">Pay Month</span><span class="info-val">{{ details?.period?.payroll_month || '—' }}</span></div>
          <div class="info-row"><span class="info-key">Status</span>
            <span class="status-chip" :class="statusClass(details?.period?.status)">{{ capitalize(details?.period?.status) }}</span>
          </div>
        </div>
      </div>

      <!-- ══ EARNINGS + DEDUCTIONS (two columns) ══ -->
      <div class="ed-grid">

        <!-- Earnings -->
        <div class="ed-col">
          <div class="ed-hdr ed-hdr--earn">
            <font-awesome-icon icon="arrow-up" class="hdr-icon" /> Earnings
          </div>
          <table class="ed-table">
            <thead>
              <tr>
                <th>Description</th>
                <th class="align-r">{{ currencySymbol }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in details?.salary_components || []" :key="'sc' + i">
                <td>{{ c.name }}</td>
                <td class="align-r">{{ fmt(c.amount) }}</td>
              </tr>
              <tr v-for="(c, i) in details?.additional_earnings || []" :key="'ae' + i" class="row-earn-extra">
                <td>
                  {{ c.name }}
                  <span class="tag-taxable" v-if="c.is_taxable">taxable</span>
                  <span class="tag-nontax" v-else>non-taxable</span>
                </td>
                <td class="align-r earn-val">{{ fmt(c.amount) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="total-lbl">Total Earnings</td>
                <td class="align-r total-earn">{{ fmt(details?.gross) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Deductions -->
        <div class="ed-col">
          <div class="ed-hdr ed-hdr--deduct">
            <font-awesome-icon icon="arrow-down" class="hdr-icon" /> Deductions
          </div>
          <table class="ed-table">
            <thead>
              <tr>
                <th>Description</th>
                <th class="align-r">{{ currencySymbol }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in details?.statutory_deductions || []" :key="'sd' + i">
                <td>
                  {{ c.name }}
                  <span v-if="c.is_effective_rate && c.rate" class="rate-badge rate-badge--effective">{{ c.rate }}% effective</span>
                  <span v-else-if="c.rate" class="rate-badge">{{ c.rate }}%</span>
                </td>
                <td class="align-r deduct-val">{{ fmt(c.amount) }}</td>
              </tr>
              <tr v-for="(c, i) in details?.additional_deductions || []" :key="'ad' + i" class="row-deduct-extra">
                <td>{{ c.name }}</td>
                <td class="align-r deduct-val">{{ fmt(c.amount) }}</td>
              </tr>
              <tr v-if="!hasDeductions" class="row-empty">
                <td colspan="2" class="empty-cell">No deductions</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="total-lbl">Total Deductions</td>
                <td class="align-r total-deduct">{{ fmt(totalDeductions) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- ══ SUMMARY BAR ══ -->
      <div class="summary-bar">
        <div class="summary-cell">
          <p class="summary-label">Gross Pay</p>
          <p class="summary-amount">{{ currencySymbol }}{{ fmt(details?.gross) }}</p>
        </div>
        <div class="summary-sep"></div>
        <div class="summary-cell">
          <p class="summary-label">Taxable Income</p>
          <p class="summary-amount">{{ currencySymbol }}{{ fmt(details?.taxable_income) }}</p>
        </div>
        <div class="summary-sep"></div>
        <div class="summary-cell">
          <p class="summary-label">Total Deductions</p>
          <p class="summary-amount summary-amount--deduct">{{ currencySymbol }}{{ fmt(totalDeductions) }}</p>
        </div>
        <div class="summary-sep"></div>
        <div class="summary-cell summary-cell--net">
          <p class="summary-label summary-label--net">Net Pay</p>
          <p class="summary-amount summary-amount--net">{{ currencySymbol }}{{ fmt(details?.net) }}</p>
        </div>
      </div>

      <!-- ══ STATUTORY PAYMENTS (SSP / SMP / SPP — UK) ══ -->
      <div class="statutory-band" v-if="showStatutoryPayments">
        <p class="statutory-title"><font-awesome-icon icon="file-medical" class="me-1" /> Statutory Payments</p>
        <div class="statutory-cols">
          <div class="statutory-item" v-if="Number(details?.ssp) > 0">
            <span class="statutory-key">SSP</span>
            <span class="statutory-val">{{ currencySymbol }}{{ fmt(details?.ssp) }}</span>
            <span class="statutory-badge">Sick Pay</span>
          </div>
          <div class="statutory-item" v-if="Number(details?.smp) > 0">
            <span class="statutory-key">SMP</span>
            <span class="statutory-val">{{ currencySymbol }}{{ fmt(details?.smp) }}</span>
            <span class="statutory-badge">Maternity Pay</span>
          </div>
          <div class="statutory-item" v-if="Number(details?.spp) > 0">
            <span class="statutory-key">SPP</span>
            <span class="statutory-val">{{ currencySymbol }}{{ fmt(details?.spp) }}</span>
            <span class="statutory-badge">Paternity Pay</span>
          </div>
        </div>
      </div>

      <!-- ══ EMPLOYER CONTRIBUTIONS (UK / conditional) ══ -->
      <div class="employer-band" v-if="showEmployerContributions">
        <p class="employer-title"><font-awesome-icon icon="building" class="me-1" /> Employer Contributions</p>
        <div class="employer-cols">
          <div class="employer-item" v-if="Number(details?.employer_ni) > 0">
            <span class="employer-key">Employer NI</span>
            <span class="employer-val">{{ currencySymbol }}{{ fmt(details?.employer_ni) }}</span>
          </div>
          <div class="employer-item" v-if="Number(details?.employer_pension) > 0">
            <span class="employer-key">Employer Pension</span>
            <span class="employer-val">{{ currencySymbol }}{{ fmt(details?.employer_pension) }}</span>
          </div>
        </div>
      </div>

      <!-- ══ FOOTER ══ -->
      <div class="slip-footer">
        <font-awesome-icon icon="info-circle" class="me-1" />
        This is a system-generated payslip and does not require a signature.
        <span v-if="details?.period?.business?.email2"> For queries, contact <strong>{{ details.period.business.email2 }}</strong>.</span>
      </div>

    </div>

    <!-- Error state -->
    <div v-else class="empty-doc">
      <div class="empty-icon"><font-awesome-icon icon="exclamation-triangle" /></div>
      <p class="empty-title">Payslip not found</p>
      <p class="empty-sub">This payslip could not be loaded.</p>
      <button type="button" @click="$router.go(-1)" class="btn-outline mt-3">Go Back</button>
    </div>

  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import jsPDF       from 'jspdf';
import html2canvas from 'html2canvas';
import { reusableMixin } from '../../../../lib/reusableMixin';
import { useStore } from 'vuex';
import axios from 'axios';
import host from '../../../../main';

export default {
  mixins: [reusableMixin],

  data() {
    return {
      toast:   useToast(),
      store:   useStore(),
      loading: true,
      details: null,
      host:    host.hostName,
    };
  },

  computed: {
    currencySymbol() {
      const map = { NG: '₦', GB: '£', US: '$', EU: '€' };
      return map[this.details?.period?.country] || '₦';
    },
    totalDeductions() {
      const d = this.details ?? {};
      return Number(d.statutory_deduction ?? 0) + Number(d.additional_deduction ?? 0);
    },
    hasDeductions() {
      return (this.details?.statutory_deductions?.length || 0) + (this.details?.additional_deductions?.length || 0) > 0;
    },
    showEmployerContributions() {
      return Number(this.details?.employer_ni ?? 0) > 0 || Number(this.details?.employer_pension ?? 0) > 0;
    },
    showStatutoryPayments() {
      return Number(this.details?.ssp ?? 0) > 0
          || Number(this.details?.smp ?? 0) > 0
          || Number(this.details?.spp ?? 0) > 0;
    },
  },

  created() { this.getPayslip(); },

  methods: {
    getPayslip() {
      this.loading = true;
      axios.get(`/payroll/payslip/${this.$route.params.id}`)
        .then(response => {
          const data = response.data.data;
          if (data) {
            const parse = f => typeof f === 'string' ? (JSON.parse(f) || []) : (f || []);
            data.salary_components     = parse(data.salary_components);
            data.additional_earnings   = parse(data.additional_earnings);
            data.additional_deductions = parse(data.additional_deductions);
            data.statutory_deductions  = parse(data.statutory_deductions);
          }
          this.details = data;
          this.loading = false;
        })
        .catch(() => { this.loading = false; this.toast.error('Could not load payslip'); });
    },

    fmt(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    capitalize(s) {
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    },

    statusClass(status) {
      return {
        open:      'chip--open',
        processed: 'chip--processed',
        approved:  'chip--approved',
        posted:    'chip--posted',
        paid:      'chip--paid',
      }[status] ?? '';
    },

    formatDate(d) {
      if (!d) return '—';
      const dt  = new Date(d);
      const day = dt.getDate();
      const ord = ['st','nd','rd'][((day % 100 - 20) % 10) | 0] || 'th';
      return `${day}${ord} ${dt.toLocaleString('en-GB', { month: 'short' })} ${dt.getFullYear()}`;
    },

    async downloadPDF() {
      const el     = this.$refs.payslipRef;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const pdf    = new jsPDF('p', 'mm', 'a4');
      const w      = pdf.internal.pageSize.getWidth();
      const h      = (canvas.height * w) / canvas.width;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h);
      const name = `${this.details?.period?.payroll_month}_${this.details?.employee?.first_name}_${this.details?.employee?.last_name}_Payslip.pdf`;
      pdf.save(name);
    },
  },
};
</script>

<style scoped>
/* ── utilities ── */
.align-r { text-align: right; }
.fw      { font-weight: 700; }
.me-1    { margin-right: 5px; }
.mt-3    { margin-top: 12px; }
.icon-red { color: #dc2626; margin-right: 6px; }

/* ── page header ── */
.page-header       { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.page-header-left  { display: flex; align-items: flex-start; gap: 12px; }
.page-title        { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 3px; display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.page-sub          { font-size: 13px; color: #64748b; margin: 0; }
.page-actions      { display: flex; gap: 8px; align-items: center; }
.btn-back          { background: none; border: none; color: #64748b; font-size: 16px; cursor: pointer; padding: 6px 8px 0 0; }
.btn-back:hover    { color: #0f172a; }
.btn-outline       { display: inline-flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #e2e8f0; color: #334155; font-size: 13px; font-weight: 500; padding: 8px 16px; border-radius: 8px; cursor: pointer; }
.btn-outline:hover { background: #f8fafc; }

/* ── status chips ── */
.status-chip, .ref-pill {
  display: inline-flex; align-items: center; border-radius: 20px;
  padding: 2px 10px; font-size: 11.5px; font-weight: 600; border: 1px solid transparent;
}
.ref-pill        { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.chip--open      { background: #fffbeb; color: #b45309; border-color: #fde68a; }
.chip--processed { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }
.chip--approved  { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.chip--posted    { background: #faf5ff; color: #7c3aed; border-color: #ddd6fe; }
.chip--paid      { background: #f0fdfa; color: #0f766e; border-color: #99f6e4; }

/* ── skeleton ── */
.skeleton-wrap { max-width: 920px; margin: auto; display: flex; flex-direction: column; gap: 14px; }
.sk-row        { display: flex; gap: 14px; }
.sk            { background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; border-radius: 10px; }
@keyframes shimmer { to { background-position: -200% 0; } }
.sk-hdr    { height: 110px; }
.sk-half   { flex: 1; height: 80px; }
.sk-body   { height: 280px; }
.sk-footer { height: 60px; }

/* ── document wrapper ── */
.slip {
  max-width: 920px; margin: auto;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 16px;
  overflow: hidden; box-shadow: 0 1px 8px rgba(0,0,0,.05);
}

/* ── document header ── */
.slip-head-accent { height: 6px; background: linear-gradient(90deg, #1d4ed8, #7c3aed); }
.slip-head-body   { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 20px; padding: 24px 28px 20px; border-bottom: 1px solid #e2e8f0; }
.slip-company     { display: flex; align-items: flex-start; gap: 14px; }
.slip-logo        { width: 64px; height: 64px; object-fit: contain; border-radius: 10px; border: 1px solid #e2e8f0; }
.slip-logo-placeholder { width: 64px; height: 64px; border-radius: 10px; background: #eff6ff; color: #1d4ed8; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; }
.slip-company-name { font-size: 17px; font-weight: 800; color: #0f172a; margin: 0 0 4px; }
.slip-company-meta { font-size: 12px; color: #64748b; margin: 0 0 1px; }
.slip-head-right  { text-align: right; }
.slip-title       { font-size: 30px; font-weight: 900; letter-spacing: .1em; color: #1d4ed8; margin-bottom: 12px; }
.slip-meta-block  { display: flex; flex-direction: column; gap: 4px; }
.slip-meta-row    { display: flex; justify-content: flex-end; gap: 8px; font-size: 12.5px; }
.slip-meta-key    { color: #94a3b8; font-weight: 500; }
.slip-meta-val    { color: #1e293b; font-weight: 600; }

/* ── employee / payment info strip ── */
.info-strip   { display: flex; align-items: stretch; gap: 0; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
.info-col     { flex: 1; padding: 18px 28px; }
.info-divider { width: 1px; background: #e2e8f0; flex-shrink: 0; }
.info-heading { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #94a3b8; margin: 0 0 10px; }
.info-row     { display: flex; align-items: baseline; gap: 8px; margin-bottom: 5px; }
.info-key     { font-size: 12px; color: #64748b; min-width: 88px; flex-shrink: 0; }
.info-val     { font-size: 13px; color: #1e293b; }

/* ── earnings / deductions two-column grid ── */
.ed-grid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #e2e8f0; }
.ed-col  { }
.ed-col:first-child { border-right: 1px solid #e2e8f0; }

.ed-hdr         { padding: 10px 20px; font-size: 13px; font-weight: 700; display: flex; align-items: center; gap: 7px; border-bottom: 1px solid #e2e8f0; }
.ed-hdr--earn   { background: rgba(21,128,61,.06);   color: #15803d; }
.ed-hdr--deduct { background: rgba(185,28,28,.05);   color: #b91c1c; }
.hdr-icon       { font-size: 11px; }

.ed-table          { width: 100%; border-collapse: collapse; }
.ed-table thead tr { background: #f8fafc; }
.ed-table thead th { padding: 8px 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; border-bottom: 1px solid #e2e8f0; }
.ed-table tbody td { padding: 10px 20px; font-size: 13px; color: #1e293b; border-bottom: 1px solid #f1f5f9; }
.ed-table tbody tr:last-child td { border-bottom: none; }
.ed-table tfoot td { padding: 10px 20px; font-size: 13px; border-top: 2px solid #e2e8f0; background: #f8fafc; }

.earn-val    { color: #15803d; font-weight: 600; }
.deduct-val  { color: #b91c1c; font-weight: 600; }
.total-lbl   { font-weight: 700; color: #0f172a; font-size: 13px; }
.total-earn  { font-size: 15px; font-weight: 800; color: #15803d; }
.total-deduct { font-size: 15px; font-weight: 800; color: #b91c1c; }

.row-earn-extra td  { background: rgba(21,128,61,.02); }
.row-deduct-extra td { background: rgba(185,28,28,.02); }
.row-empty td { text-align: center; }
.empty-cell  { color: #94a3b8; font-size: 12.5px; padding: 16px !important; font-style: italic; }

.rate-badge             { display: inline-block; background: #f1f5f9; color: #64748b; font-size: 10.5px; font-weight: 600; border-radius: 4px; padding: 1px 5px; margin-left: 6px; }
.rate-badge--effective  { background: rgba(29,78,216,.08); color: #1d4ed8; border: 1px solid rgba(29,78,216,.2); }
.tag-taxable  { display: inline-block; background: rgba(185,28,28,.08); color: #b91c1c; font-size: 10px; font-weight: 600; border-radius: 4px; padding: 1px 5px; margin-left: 6px; }
.tag-nontax   { display: inline-block; background: #f1f5f9; color: #64748b; font-size: 10px; font-weight: 600; border-radius: 4px; padding: 1px 5px; margin-left: 6px; }

/* ── summary bar ── */
.summary-bar  { display: flex; align-items: stretch; border-bottom: 1px solid #e2e8f0; }
.summary-cell { flex: 1; padding: 16px 20px; text-align: center; }
.summary-cell--net { background: #1d4ed8; border-radius: 0; }
.summary-sep  { width: 1px; background: #e2e8f0; }
.summary-label       { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #94a3b8; margin: 0 0 4px; }
.summary-label--net  { color: rgba(255,255,255,.75); }
.summary-amount      { font-size: 17px; font-weight: 800; color: #0f172a; margin: 0; }
.summary-amount--deduct { color: #b91c1c; }
.summary-amount--net    { font-size: 22px; color: #fff; }

/* ── employer contributions ── */
.employer-band  { padding: 14px 28px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
.employer-title { font-size: 12.5px; font-weight: 700; color: #475569; margin: 0; white-space: nowrap; }
.employer-cols  { display: flex; flex-wrap: wrap; gap: 20px; }
.employer-item  { display: flex; gap: 6px; align-items: baseline; }
.employer-key   { font-size: 12.5px; color: #64748b; }
.employer-val   { font-size: 13px; font-weight: 700; color: #1e293b; }
.statutory-band   { padding: 14px 28px; background: #f0fdf4; border-bottom: 1px solid #bbf7d0; display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
.statutory-title  { font-size: 12.5px; font-weight: 700; color: #166534; margin: 0; white-space: nowrap; }
.statutory-cols   { display: flex; flex-wrap: wrap; gap: 20px; }
.statutory-item   { display: flex; gap: 6px; align-items: baseline; }
.statutory-key    { font-size: 12.5px; color: #166534; font-weight: 600; }
.statutory-val    { font-size: 13px; font-weight: 700; color: #14532d; }
.statutory-badge  { font-size: 10px; background: #dcfce7; color: #166534; border-radius: 4px; padding: 1px 6px; font-weight: 600; }

/* ── footer ── */
.slip-footer { padding: 14px 28px; text-align: center; font-size: 12px; color: #94a3b8; line-height: 1.8; }

/* ── empty / error ── */
.empty-doc  { max-width: 920px; margin: auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 64px 24px; text-align: center; }
.empty-icon { width: 56px; height: 56px; border-radius: 14px; background: rgba(185,28,28,.08); color: #b91c1c; font-size: 22px; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
.empty-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0 0 6px; }
.empty-sub   { font-size: 13px; color: #64748b; margin: 0; }

/* ── print ── */
@media print {
  .page-header { display: none !important; }
  .slip { border: none; border-radius: 0; box-shadow: none; }
}

/* ── responsive ── */
@media (max-width: 700px) {
  .slip-head-body  { flex-direction: column; }
  .slip-head-right { text-align: left; }
  .slip-title      { font-size: 22px; }
  .info-strip      { flex-direction: column; }
  .info-divider    { width: auto; height: 1px; }
  .ed-grid         { grid-template-columns: 1fr; }
  .ed-col:first-child { border-right: none; border-bottom: 1px solid #e2e8f0; }
  .summary-bar     { flex-wrap: wrap; }
  .summary-cell    { min-width: 48%; }
  .summary-sep     { display: none; }
}
</style>
